<template>
  <div class="compliance-consult-chat" :class="{ 'is-sidebar': isSidebar, 'is-hero': !isSidebar }">
    <!-- Sessions sidebar
         - Hero mode: collapsible sidebar (240px ↔ 48px rail).
         - Sidebar (dashboard) mode: 36px rail by default; clicking the
           clock icon pops the list as an overlay so users can browse and
           switch to prior consults without leaving the dashboard. -->
    <aside
      class="sessions-sidebar"
      :class="{
        collapsed: !showSessionsPanel,
        'sidebar-mode': isSidebar,
        'overlay-open': isSidebar && showSessionsPanel,
      }"
    >
      <div class="sidebar-header">
        <button class="btn-icon" @click="newSession" title="New consult">
          <i class="fas fa-plus"></i>
        </button>
        <button
          class="btn-icon"
          @click="showSessionsPanel = !showSessionsPanel"
          :title="showSessionsPanel ? 'Hide history' : 'Show history'"
        >
          <i v-if="isSidebar" class="fas fa-clock-rotate-left"></i>
          <i v-else :class="showSessionsPanel ? 'fas fa-chevron-left' : 'fas fa-chevron-right'"></i>
        </button>
      </div>
      <div v-if="showSessionsPanel" class="sessions-list">
        <div v-if="loadingSessions" class="sessions-loading">
          <i class="fas fa-spinner fa-spin"></i> Loading…
        </div>
        <div v-else-if="!groupedSessions.length" class="sessions-empty">
          No consults yet. Ask a question to start.
        </div>
        <div v-for="group in groupedSessions" :key="group.label" class="session-group">
          <div class="session-group-label">{{ group.label }}</div>
          <div
            v-for="s in group.sessions"
            :key="s.id"
            class="session-item"
            :class="{ active: s.id === currentSessionId }"
            @click="onSelectSession(s.id)"
          >
            <div class="session-title">{{ s.title || 'Untitled' }}</div>
            <div class="session-meta">
              <span>{{ s.turn_count }} turn{{ s.turn_count === 1 ? '' : 's' }}</span>
              <span v-if="s.total_evidence_cited">· {{ s.total_evidence_cited }} cites</span>
            </div>
            <button
              class="session-delete"
              @click.stop="deleteSession(s.id)"
              title="Delete session"
            >
              <i class="fas fa-trash"></i>
            </button>
          </div>
        </div>
      </div>
    </aside>
    <!-- Backdrop for sidebar-mode overlay -->
    <div
      v-if="isSidebar && showSessionsPanel"
      class="sessions-overlay-backdrop"
      @click="showSessionsPanel = false"
    ></div>

    <!-- Chat main column -->
    <div class="chat-main">
      <!-- Header -->
      <div class="chat-header">
        <div class="chat-header-title">
          <i class="fas fa-shield-halved"></i>
          <span>{{ isSidebar ? 'Compliance Agent' : 'Compliance Consult' }}</span>
        </div>
        <div class="chat-header-actions">
          <div class="depth-toggle" v-if="featureGates.deep_consult !== false">
            <button
              class="depth-btn"
              :class="{ active: defaultDepth === 'quick' }"
              @click="defaultDepth = 'quick'"
              title="Faster, fewer evidence items"
            >Quick</button>
            <button
              class="depth-btn"
              :class="{ active: defaultDepth === 'deep' }"
              @click="defaultDepth = 'deep'"
              :disabled="!featureGates.deep_consult"
              title="Comprehensive consult with full action planning"
            >Deep</button>
          </div>
          <button v-if="isSidebar" class="btn-icon" @click="openFullConsult" title="Open full-page consult">
            <i class="fas fa-up-right-from-square"></i>
          </button>
          <button class="btn-icon" @click="newSession" title="New consult">
            <i class="fas fa-plus"></i>
          </button>
        </div>
      </div>

      <!-- Token usage meter -->
      <div v-if="tokenUsage && tokenUsage.percent != null" class="usage-meter" :class="usageMeterClass">
        <div class="usage-bar"><div class="usage-fill" :style="{ width: `${Math.min(100, tokenUsage.percent)}%` }"></div></div>
        <div class="usage-text">{{ tokenUsage.percent }}% of monthly tokens used</div>
      </div>

      <!-- Welcome state — adapts copy + example prompts based on whether
           the org actually has entities yet. An empty portfolio gets
           onboarding-style questions; a populated one gets analysis ones. -->
      <div v-if="!messages.length" class="welcome-state">
        <div class="welcome-icon"><i class="fas fa-shield-halved"></i></div>
        <h3>{{ welcomeTitle }}</h3>
        <p class="welcome-sub" v-html="welcomeSub"></p>
        <div class="example-questions">
          <button
            v-for="q in exampleQuestions"
            :key="q"
            class="example-btn"
            @click="sendExample(q)"
          >{{ q }}</button>
        </div>
      </div>

      <!-- Messages -->
      <div class="messages-container" ref="messagesEl">
        <template v-for="(msg, idx) in messages" :key="idx">
          <div class="message" :class="`role-${msg.role}`">
            <div class="message-bubble">
              <div class="message-content" v-html="renderMarkdown(msg.content)"></div>

              <!-- Meta line: evidence quality + elapsed + source count.
                   Renders only on assistant messages that actually have
                   metadata so welcome/error bubbles stay clean. -->
              <div
                v-if="msg.role === 'assistant' && messageMetaLabel(msg)"
                class="message-meta"
                :class="`quality-${msg.evidenceQuality || 'unknown'}`"
              >
                <i :class="qualityIcon(msg.evidenceQuality)"></i>
                <span>{{ messageMetaLabel(msg) }}</span>
              </div>

              <!-- Results belong to the answer that produced them. This
                   avoids a detached workspace showing results from an older
                   question after the conversation has moved on. -->
              <ComplianceResultPreview
                v-if="msg.role === 'assistant' && msg.matchedEntities !== undefined"
                :entities="msg.matchedEntities"
                :total-count="msg.missingContactTotal || 0"
                :download-label="excelDownloadLabel(msg)"
                @open-record="(subjectId) => $emit('show-entity-details', subjectId)"
                @open-documents="(payload) => $emit('show-document', payload)"
                @download-excel="downloadExcel(msg)"
              />

              <!-- Evidence (assistant only) -->
              <ComplianceEvidenceList
                v-if="msg.role === 'assistant' && msg.evidence && msg.evidence.length"
                :evidence="msg.evidence"
                @open-document="(id) => $emit('show-document', { document_id: id })"
                @open-snapshot="(subjectId) => $emit('show-entity-details', subjectId)"
              />

              <ComplianceConfirmWriteCard
                v-for="(a, ai) in confirmActions(msg)"
                :key="'confirm-' + ai"
                :action="a"
                @committed="onWriteCommitted"
              />

              <!-- Suggested actions -->
              <div
                v-if="msg.role === 'assistant' && visibleActions(msg).length"
                class="actions-row"
              >
                <button
                  v-for="(a, ai) in visibleActions(msg)"
                  :key="ai"
                  class="action-btn"
                  @click="executeAction(a, msg)"
                >
                  <i class="fas fa-bolt"></i> {{ a.label }}
                </button>
              </div>

              <!-- Follow-up prompts -->
              <div
                v-if="msg.role === 'assistant' && msg.followUps && msg.followUps.length"
                class="follow-ups"
              >
                <div class="follow-ups-label">Follow-up questions:</div>
                <button
                  v-for="(f, fi) in msg.followUps"
                  :key="fi"
                  class="follow-up-btn"
                  @click="sendExample(f)"
                >{{ f }}</button>
              </div>

              <!-- Per-message toolbar (assistant) -->
              <div
                v-if="msg.role === 'assistant'"
                class="message-toolbar"
              >
                <button class="toolbar-btn" @click="copyMessage(msg)" title="Copy answer">
                  <i class="fas fa-copy"></i>
                </button>
                <button class="toolbar-btn" @click="downloadMessage(msg)" title="Download as Markdown">
                  <i class="fas fa-download"></i>
                </button>
                <button
                  class="toolbar-btn"
                  @click="saveCurrentSession"
                  :disabled="!currentSessionId"
                  title="Save this consult to library"
                >
                  <i class="fas fa-bookmark"></i>
                </button>
                <button
                  class="toolbar-btn"
                  :class="{ active: (feedbackFor === idx && feedbackKind === 'up') || msg.feedback?.kind === 'up' }"
                  @click="recordFeedback(idx, 'up')"
                  :title="msg.feedback?.kind === 'up' ? 'Recorded as helpful' : 'Helpful'"
                  :disabled="msg.feedback?.kind === 'up'"
                >
                  <i class="fas fa-thumbs-up"></i>
                </button>
                <button
                  class="toolbar-btn"
                  :class="{ active: (feedbackFor === idx && feedbackKind === 'down') || msg.feedback?.kind === 'down' }"
                  @click="recordFeedback(idx, 'down')"
                  :title="msg.feedback?.kind === 'down' ? 'Correction recorded' : 'Not helpful — train the agent'"
                >
                  <i class="fas fa-thumbs-down"></i>
                </button>
              </div>

              <!-- Inline correction box -->
              <div v-if="feedbackFor === idx && feedbackKind === 'down'" class="correction-box">
                <textarea
                  v-model="correctionText"
                  rows="3"
                  placeholder="What did the agent get wrong? (your correction trains the agent)"
                ></textarea>
                <div class="correction-actions">
                  <button class="btn-secondary" @click="cancelFeedback">Cancel</button>
                  <button class="btn-primary" @click="submitCorrection()">Submit correction</button>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Agent phase indicator -->
        <div v-if="agentPhases.length" class="agent-phases">
          <div v-for="(p, i) in agentPhases" :key="i" class="phase-line" :class="{ active: i === agentPhases.length - 1 }">
            <i :class="p.done ? 'fas fa-check' : 'fas fa-spinner fa-spin'"></i>
            <span>{{ p.message }}</span>
          </div>
        </div>

        <!-- Engagement prompt (honest opt-in on weak results) -->
        <div v-if="engagementPrompt" class="engagement-card">
          <div class="engagement-title">
            <i class="fas fa-circle-question"></i>
            {{ engagementPrompt.message }}
          </div>
          <div class="engagement-options">
            <button
              v-for="(o, i) in engagementPrompt.options"
              :key="i"
              class="engagement-btn"
              @click="applyEngagement(o)"
            >{{ o.label }}</button>
          </div>
        </div>

        <!-- Refine prompt -->
        <div v-if="refinePrompt" class="refine-card">
          <div class="refine-title">
            <i class="fas fa-sliders"></i>
            {{ refinePrompt.message }}
          </div>
          <div class="refine-suggestions">
            <button
              v-for="(s, i) in refinePrompt.suggestions"
              :key="i"
              class="refine-chip"
              @click="applyRefine(s)"
            >{{ s }}</button>
          </div>
        </div>
      </div>

      <!-- Composer -->
      <div class="composer">
        <textarea
          v-model="composerText"
          class="composer-input"
          :placeholder="placeholder"
          rows="2"
          @keydown.enter.exact.prevent="send"
          @keydown.shift.enter.exact="composerText += '\n'"
          :disabled="sending"
        ></textarea>
        <button class="send-btn" @click="send" :disabled="sending || !composerText.trim()">
          <i v-if="sending" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-paper-plane"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, nextTick, defineExpose } from 'vue'
import { debounce } from 'lodash-es'
import api from '@/services/api'
import authService from '@/services/auth'
import ComplianceEvidenceList from './ComplianceEvidenceList.vue'
import ComplianceResultPreview from './ComplianceResultPreview.vue'
import ComplianceConfirmWriteCard from './ComplianceConfirmWriteCard.vue'

export default {
  name: 'ComplianceConsultChat',
  components: { ComplianceEvidenceList, ComplianceResultPreview, ComplianceConfirmWriteCard },
  props: {
    projectId: { type: [Number, String], default: null },
    summary: { type: Object, default: () => ({}) },
    isSidebar: { type: Boolean, default: false },
    initialDepth: { type: String, default: 'quick' },
    initialQuery: { type: String, default: '' },
  },
  emits: [
    'filter-entities',
    'select-entities',
    'notify',
    'show-results',
    'refresh-data',
    'create-entity',
    'show-entity-details',
    'show-document',
    'open-full-consult',
  ],
  setup(props, { emit }) {
    // ── State ─────────────────────────────────────────────────────────
    const messages = ref([])           // [{role, content, evidence, actions, followUps, ...}]
    const composerText = ref('')
    const sending = ref(false)
    const defaultDepth = ref(props.initialDepth || 'quick')
    const currentSessionId = ref(null)
    const sessions = ref([])
    const loadingSessions = ref(false)
    // In hero mode the sessions list is open by default; in dashboard
    // sidebar mode it starts collapsed so the chat keeps the available
    // width and the user opens history via the clock icon.
    const showSessionsPanel = ref(!props.isSidebar)
    const agentPhases = ref([])
    const refinePrompt = ref(null)
    const engagementPrompt = ref(null)
    const featureGates = ref({})
    const tokenUsage = ref(null)

    const feedbackFor = ref(null)
    const feedbackKind = ref(null)
    const correctionText = ref('')

    const messagesEl = ref(null)

    // Treat 0 entities as "empty portfolio" — show onboarding questions
    // instead of analysis ones so the agent is useful before there is data.
    const isEmptyPortfolio = computed(() => {
      const total = props.summary?.total_entities
      return typeof total === 'number' && total === 0
    })

    const exampleQuestions = computed(() => {
      if (isEmptyPortfolio.value) {
        return [
          'How do I add my first entity (subcontractor or vendor)?',
          'What documents should I collect to track compliance?',
          'How does the compliance health score work?',
          'Walk me through setting up a project and its requirements.',
        ]
      }
      return [
        'Which entities are below 75%?',
        'Which entities have no contacts loaded?',
        'Who is missing General Liability coverage?',
        'Show me entities trending downward this month',
        'Why did Store 13254 fail its last check?',
      ]
    })

    const welcomeTitle = computed(() =>
      isEmptyPortfolio.value
        ? 'Welcome — let\u2019s get your portfolio set up'
        : 'Ask anything about your compliance health'
    )

    const welcomeSub = computed(() =>
      isEmptyPortfolio.value
        ? 'You don\u2019t have any entities yet. The agent can walk you through onboarding, document collection, and how compliance scoring works. Once you add entities, it will start answering with <strong>real evidence</strong> from your portfolio.'
        : 'Answers use certificates on file, health snapshots, check requirements, gap notifications, and earlier conversations.'
    )

    const placeholder = computed(() =>
      props.isSidebar
        ? 'Ask about your compliance health…'
        : 'Ask about coverage, expirations, or which entities are out of compliance…'
    )

    const usageMeterClass = computed(() => {
      const p = tokenUsage.value?.percent ?? 0
      if (p >= 90) return 'usage-critical'
      if (p >= 70) return 'usage-warning'
      return 'usage-ok'
    })

    // ── Sessions sidebar grouping ─────────────────────────────────────
    const groupedSessions = computed(() => {
      const today = []
      const yesterday = []
      const earlier = []
      const now = new Date()
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const startOfYesterday = new Date(startOfToday)
      startOfYesterday.setDate(startOfYesterday.getDate() - 1)

      sessions.value.forEach(s => {
        const d = new Date(s.updated_at)
        if (d >= startOfToday) today.push(s)
        else if (d >= startOfYesterday) yesterday.push(s)
        else earlier.push(s)
      })

      const out = []
      if (today.length) out.push({ label: 'Today', sessions: today })
      if (yesterday.length) out.push({ label: 'Yesterday', sessions: yesterday })
      if (earlier.length) out.push({ label: 'Earlier', sessions: earlier })
      return out
    })

    // ── API helpers ───────────────────────────────────────────────────
    const loadSessions = async () => {
      loadingSessions.value = true
      try {
        const res = await api.get('/compliance-health/consult/sessions/')
        sessions.value = res.data.results || []
      } catch (e) {
        console.warn('Failed to load consult sessions', e)
      } finally {
        loadingSessions.value = false
      }
    }

    const loadSession = async (sessionId) => {
      try {
        const res = await api.get(`/compliance-health/consult/sessions/${sessionId}/`)
        const data = res.data
        currentSessionId.value = data.id
        messages.value = (data.turns || []).map(t => ({
          role: t.role,
          content: t.content || '',
          evidence: t.evidence || [],
          actions: t.actions || [],
          matchedEntities: Object.prototype.hasOwnProperty.call(t, 'matched_entities')
            ? (t.matched_entities || [])
            : undefined,
          resultKind: t.result_kind || t.resultKind || '',
          missingContactTotal: t.missing_contact_total || t.missingContactTotal || 0,
          followUps: [],
          evidenceQuality: t.evidenceQuality || t.evidence_quality || '',
          elapsedMs: t.elapsedMs || t.elapsed_ms || null,
          feedback: t.feedback || null,
        }))
        await scrollToBottom()
      } catch (e) {
        console.warn('Failed to load session', e)
      }
    }

    // In sidebar/overlay mode, picking a session closes the overlay so
    // the chat is immediately visible again. In hero mode the sidebar
    // stays open.
    const onSelectSession = async (sessionId) => {
      await loadSession(sessionId)
      if (props.isSidebar) showSessionsPanel.value = false
    }

    const deleteSession = async (sessionId) => {
      if (!confirm('Delete this consult session?')) return
      try {
        await api.delete(`/compliance-health/consult/sessions/${sessionId}/`)
        sessions.value = sessions.value.filter(s => s.id !== sessionId)
        if (currentSessionId.value === sessionId) newSession()
      } catch (e) {
        console.warn('Failed to delete session', e)
      }
    }

    const newSession = () => {
      currentSessionId.value = null
      messages.value = []
      agentPhases.value = []
      refinePrompt.value = null
      engagementPrompt.value = null
      composerText.value = ''
      feedbackFor.value = null
      feedbackKind.value = null
    }

    // Honest engagement: re-run the last query to search more broadly.
    const applyEngagement = (option) => {
      if (!option) return
      const lastUser = [...messages.value].reverse().find(m => m.role === 'user')
      if (!lastUser) return
      engagementPrompt.value = null
      if (option.action === 'search_broader') {
        sending.value = true
        submitQueryStream(lastUser.content, 'Look across the whole portfolio, including weaker matches.').finally(() => {
          sending.value = false
          scrollToBottom()
        })
      }
    }

    const saveCurrentSession = async () => {
      if (!currentSessionId.value) return
      try {
        await api.post(`/compliance-health/consult/sessions/${currentSessionId.value}/save/`, {})
        // Optimistic update on the local list
        const s = sessions.value.find(x => x.id === currentSessionId.value)
        if (s) s.is_saved = true
      } catch (e) {
        console.warn('Failed to save session', e)
      }
    }

    // ── Streaming send ────────────────────────────────────────────────
    const send = async () => {
      const text = composerText.value.trim()
      if (!text || sending.value) return
      composerText.value = ''
      sending.value = true

      messages.value.push({
        role: 'user',
        content: text,
        evidence: [], actions: [], followUps: [], evidenceQuality: '',
      })
      await scrollToBottom()

      try {
        await submitQueryStream(text)
      } finally {
        sending.value = false
        await scrollToBottom()
        loadSessions() // refresh session list to pick up new session
        loadTokenUsage()
      }
    }

    const sendExample = (q) => {
      composerText.value = q
      send()
    }

    const applyRefine = (suggestion) => {
      // Re-run the last user query with the suggestion appended as relevance_context
      const lastUser = [...messages.value].reverse().find(m => m.role === 'user')
      if (!lastUser) return
      sending.value = true
      submitQueryStream(lastUser.content, suggestion).finally(() => {
        sending.value = false
        scrollToBottom()
      })
    }

    const submitQueryStream = async (queryText, ctxOverride) => {
      const baseURL = (process.env.VUE_APP_API_URL || 'http://localhost:8000/api').replace(/\/$/, '')
      const token = authService.getToken()
      const ctx = ctxOverride ?? null

      agentPhases.value = []
      refinePrompt.value = null
      engagementPrompt.value = null

      let streamSucceeded = false
      try {
        const response = await fetch(`${baseURL}/compliance-health/consult/ask-stream/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            query: queryText,
            session_id: currentSessionId.value || null,
            consult_depth: defaultDepth.value,
            project_id: props.projectId || null,
            relevance_context: ctx || null,
          }),
        })

        if (response.status === 403) {
          const body = await response.json().catch(() => ({}))
          messages.value.push({
            role: 'assistant',
            content: body.feature === 'deep_consult'
              ? 'Deep Consult requires a Standard subscription or above. You can still use Quick Consult, or [upgrade your plan](/settings/billing) to review more of the portfolio.'
              : (body.error || 'This feature is not available on your current plan.'),
            evidence: [], actions: [], followUps: [], evidenceQuality: '',
          })
          return
        }
        if (response.status === 429) {
          messages.value.push({
            role: 'assistant',
            content: "You've used your token quota for this billing period. [Upgrade your plan](/settings/billing) to continue.",
            evidence: [], actions: [], followUps: [], evidenceQuality: '',
          })
          return
        }
        if (!response.ok || !response.body) {
          throw new Error(`HTTP ${response.status}`)
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        streamSucceeded = true

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop()
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const event = JSON.parse(line.slice(6))
                handleStreamEvent(event)
                if (event.type === 'done') break
              } catch { /* ignore malformed event */ }
            }
          }
        }
      } catch (streamErr) {
        console.warn('SSE stream failed, falling back to /ask/:', streamErr)
        streamSucceeded = false
      }

      if (!streamSucceeded) {
        try {
          const res = await api.post('/compliance-health/consult/ask/', {
            query: queryText,
            session_id: currentSessionId.value || null,
            consult_depth: defaultDepth.value,
            project_id: props.projectId || null,
            // Pass relevance_context through the sync fallback too so refine
            // suggestions still narrow the answer when SSE is buffered or
            // blocked by an intermediary proxy.
            relevance_context: ctx || null,
          })
          const data = res.data
          currentSessionId.value = data.session_id
          messages.value.push({
            role: 'assistant',
            content: data.answer || '',
            evidence: data.evidence || [],
            actions: data.actions || [],
            matchedEntities: data.matched_entities || [],
            resultKind: data.result_kind || '',
            missingContactTotal: data.missing_contact_total || 0,
            followUps: data.follow_up_questions || [],
            evidenceQuality: data.evidence_quality || '',
            elapsedMs: data.elapsed_ms || null,
          })
          const matchedEntities = data.matched_entities || []
          if (props.isSidebar) {
            emit('show-results', {
              title: 'Matched compliance records',
              subtitle: `Found ${matchedEntities.length} matching records`,
              entities: matchedEntities,
            })
          }
        } catch (e) {
          messages.value.push({
            role: 'assistant',
            content: 'Sorry, I encountered an error. Please try again.',
            evidence: [], actions: [], followUps: [], evidenceQuality: '',
            elapsedMs: null,
          })
        }
      }

      await scrollToBottom()
    }

    const handleStreamEvent = (event) => {
      switch (event.type) {
        case 'feature_gates':
          featureGates.value = event.gates || {}
          break
        case 'status':
          // Mark previous as done, push new
          agentPhases.value = agentPhases.value.map(p => ({ ...p, done: true }))
          agentPhases.value.push({
            phase: event.phase,
            message: event.message,
            done: false,
          })
          break
        case 'refine_prompt':
          refinePrompt.value = {
            message: event.message,
            suggestions: event.suggestions || [],
          }
          break
        case 'engagement_prompt':
          // Portfolio follow-up only. Live-source search belongs to other consults.
          engagementPrompt.value = {
            gate: event.gate,
            message: event.message,
            options: (event.options || []).filter(o => o.action === 'search_broader'),
          }
          break
        case 'result': {
          const d = event.data || {}
          currentSessionId.value = d.session_id
          messages.value.push({
            role: 'assistant',
            content: d.answer || '',
            evidence: d.evidence || [],
            actions: d.actions || [],
            matchedEntities: d.matched_entities || [],
            resultKind: d.result_kind || '',
            missingContactTotal: d.missing_contact_total || 0,
            followUps: d.follow_up_questions || [],
            evidenceQuality: d.evidence_quality || '',
            elapsedMs: d.elapsed_ms || null,
          })
          // Sidebar dashboards use a detached results workspace; hero/embedded
          // consult keeps results on the message (ComplianceResultPreview).
          const matchedEntities = d.matched_entities || []
          if (props.isSidebar) {
            emit('show-results', {
              title: 'Matched compliance records',
              subtitle: `Found ${matchedEntities.length} matching records`,
              entities: matchedEntities,
            })
          }
          // Phases all done
          agentPhases.value = agentPhases.value.map(p => ({ ...p, done: true }))
          // Clear after a beat so the user sees the final state briefly
          setTimeout(() => { agentPhases.value = [] }, 500)
          break
        }
        case 'error': {
          // When the stream knows it failed because of a quota/feature
          // gate, mirror the sync /ask/ upgrade CTA shape so the user
          // gets the same actionable message in both paths.
          let content = event.message || 'Stream error.'
          if (event.feature === 'token_quota' || event.upgrade_url) {
            const url = event.upgrade_url || '/settings/billing'
            content = `${content} [Upgrade your plan](${url}) to continue.`
          } else if (event.feature === 'deep_consult') {
            content = 'Deep Consult requires a Standard subscription or above. You can still use Quick Consult, or [upgrade your plan](/settings/billing) to review more of the portfolio.'
          }
          messages.value.push({
            role: 'assistant',
            content,
            evidence: [], actions: [], followUps: [], evidenceQuality: '',
          })
          agentPhases.value = []
          break
        }
        case 'done':
          break
      }
    }

    // ── Action button dispatch ────────────────────────────────────────
    // ``msg`` is the assistant message that produced this action — we
    // use it for the notify case to pull the LLM-drafted email body
    // out of the answer when the backend didn't already provide one.
    const executeAction = (action, msg = null) => {
      if (!action || !action.type) return
      const payload = action.payload || {}
      switch (action.type) {
        case 'filter-entities':
          emit('filter-entities', payload)
          break
        case 'select-entities':
          emit('select-entities', payload)
          break
        case 'notify': {
          // Always attach an ``email_draft`` so the dashboard's bulk-
          // notify modal opens with usable text. Priority order:
          //   1. Backend-provided structured draft (preferred).
          //   2. The LLM's prose answer text (gives user the email
          //      they just saw above the action button).
          // We never overwrite a backend draft, just augment it with
          // the assistant_text so the modal can offer "use AI text"
          // as a one-click swap if the user prefers it.
          const enriched = { ...payload }
          if (msg && msg.role === 'assistant' && typeof msg.content === 'string') {
            enriched.assistant_text = msg.content
            if (!enriched.email_draft) {
              enriched.email_draft = {
                subject: 'Action Required: Compliance Coverage Update',
                body: msg.content,
                source: 'assistant_text',
              }
            } else if (!enriched.email_draft.assistant_text) {
              enriched.email_draft.assistant_text = msg.content
            }
          }
          emit('notify', enriched)
          break
        }
        case 'show-results':
          if (props.isSidebar) {
            emit('show-results', payload)
          }
          break
        case 'refresh-data':
          emit('refresh-data', payload)
          break
        case 'refresh_health_summaries':
        case 'refresh-health-summaries': {
          // Fire API from the chat surface, then ask parent to reload KPIs.
          (async () => {
            try {
              const body = {
                project_id: props.projectId,
                subject_ids: payload.subject_ids || payload.entityIds || [],
                force: !!payload.force,
                include_llm_summary: !!payload.include_llm_summary,
              }
              if (!body.subject_ids.length) delete body.subject_ids
              const res = await api.post(
                '/compliance-health/agent/refresh-health-summaries/',
                body
              )
              messages.value.push({
                role: 'assistant',
                content: res.data?.message
                  || `Queued health refresh for ${res.data?.queued || 0} entities.`,
                evidence: [],
                actions: [],
                followUps: [],
                evidenceQuality: '',
              })
              emit('refresh-data', payload)
            } catch (err) {
              messages.value.push({
                role: 'assistant',
                content: `Failed to refresh health summaries: ${err.response?.data?.error || err.message}`,
                evidence: [],
                actions: [],
                followUps: [],
                evidenceQuality: '',
              })
            }
          })()
          break
        }
        case 'create-entity':
          emit('create-entity', payload)
          break
        case 'show-entity-details':
          emit('show-entity-details', payload.subject_id || payload)
          break
        // Accept both action types so changes in the backend's emitted
        // string don't silently break the docs button (legacy and new
        // services have used both names).
        case 'show-document':
        case 'show-documents':
          emit('show-document', payload)
          break
        case 'export_missing_contacts':
          downloadExcel(msg)
          break
        case 'retry_unfinished_reviews': {
          const mode = payload.mode === 'after_fix' ? 'after_fix' : 'infrastructure'
          api.post('/agent-activity/unfinished-reviews/retry/', {
            mode,
            project_id: payload.project_id || props.projectId || null,
            check_run_ids: payload.check_run_ids || [],
            all_orgs: !payload.project_id && !props.projectId,
          }).then((res) => {
            const queued = (res.data && res.data.queued) || []
            const verb = mode === 'after_fix'
              ? 'Queued after the fix'
              : 'Queued infrastructure retries'
            messages.value.push({
              role: 'assistant',
              content: `${verb}: ${queued.length}. They will run again and can finish once the check completes.`,
              evidence: [],
              actions: [],
              followUps: [],
              evidenceQuality: '',
            })
          }).catch((err) => {
            messages.value.push({
              role: 'assistant',
              content: `Could not queue those reviews: ${err.response?.data?.error || err.message}`,
              evidence: [],
              actions: [],
              followUps: [],
              evidenceQuality: '',
            })
          })
          break
        }
        default:
          console.warn('Unknown action type:', action.type)
      }
    }

    const visibleActions = (msg) => (
      (msg?.actions || []).filter(action => (
        action.type !== 'filter-entities'
        && action.type !== 'export_missing_contacts'
        && action.type !== 'confirm_write'
      ))
    )

    const confirmActions = (msg) => (
      (msg?.actions || []).filter(action => action.type === 'confirm_write')
    )

    const onWriteCommitted = (message) => {
      if (!message) return
      messages.value.push({
        role: 'assistant',
        content: message,
        evidence: [],
        actions: [],
        followUps: [],
        evidenceQuality: '',
      })
    }

    const isMissingContactExport = (msg) => (
      msg?.resultKind === 'missing_contacts'
      || (msg?.actions || []).some(action => action.type === 'export_missing_contacts')
    )

    const excelDownloadLabel = (msg) => {
      const shown = (msg?.matchedEntities || []).length
      if (!shown) return ''
      const total = Number(msg?.missingContactTotal || 0)
      if (isMissingContactExport(msg) && total > shown) {
        return `Download all ${total} for the client`
      }
      if (isMissingContactExport(msg)) return 'Download Excel for the client'
      return 'Download Excel'
    }

    const saveBlob = (data, filename, mime) => {
      const blob = data instanceof Blob ? data : new Blob([data], { type: mime })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }

    const downloadShownEntities = async (entities) => {
      const excelModule = await import('exceljs')
      const ExcelJS = excelModule.default || excelModule
      const workbook = new ExcelJS.Workbook()
      const sheet = workbook.addWorksheet('Matching records')
      sheet.addRow(['Company', 'Score', 'Status', 'What is missing', 'Subject ID'])
      entities.forEach((entity) => {
        sheet.addRow([
          entity.entity_name || entity.name || '',
          entity.current_score ?? entity.score ?? '',
          entity.status_label || entity.status || '',
          entity.gap_label || '',
          entity.subject_id || '',
        ])
      })
      const buffer = await workbook.xlsx.writeBuffer()
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '')
      saveBlob(
        buffer,
        `compliance_records_${stamp}.xlsx`,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      )
    }

    const downloadExcel = async (msg) => {
      if (isMissingContactExport(msg) && props.projectId) {
        try {
          const response = await api.get('/compliance-health/export-missing-contacts/', {
            params: { project_id: props.projectId },
            responseType: 'blob',
          })
          const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '')
          saveBlob(
            response.data,
            `contacts_needed_${stamp}.xlsx`,
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          )
        } catch (err) {
          console.warn('contact spreadsheet download failed', err)
          messages.value.push({
            role: 'assistant',
            content: 'Could not download the contact spreadsheet. Ask the question again, then use Download Excel for the client.',
            evidence: [],
            actions: [],
            followUps: [],
            evidenceQuality: '',
          })
        }
        return
      }
      const entities = msg?.matchedEntities || []
      if (!entities.length) return
      await downloadShownEntities(entities)
    }

    // ── Feedback / corrections ────────────────────────────────────────
    // Posts to /sessions/<id>/feedback/ which writes a ConsultFeedback row
    // AND an AgentMemory row (procedural for thumbs-down corrections, belief
    // for thumbs-up) so the agent actually learns from the rating.
    const submitFeedback = async (idx, kind, correction) => {
      if (!currentSessionId.value) return
      try {
        await api.post(
          `/compliance-health/consult/sessions/${currentSessionId.value}/feedback/`,
          {
            kind,
            turn_index: idx,
            correction: correction || '',
          }
        )
        // Annotate the local message so the toolbar reflects state on
        // subsequent renders without needing a session reload.
        const msg = messages.value[idx]
        if (msg) msg.feedback = { kind }
        if (window.$toast) {
          window.$toast.success(
            kind === 'up'
              ? 'Thanks — I\u2019ll remember that worked next time.'
              : 'Thanks — I\u2019ll remember that correction next time.'
          )
        }
      } catch (e) {
        console.warn('compliance feedback failed', e)
        if (window.$toast) {
          window.$toast.error('Could not record feedback. Please try again.')
        }
      }
    }

    const recordFeedback = async (idx, kind) => {
      feedbackFor.value = idx
      feedbackKind.value = kind
      if (kind === 'up') {
        // Fire-and-forget thumbs-up — submit immediately and clear UI.
        await submitFeedback(idx, 'up')
        setTimeout(() => {
          if (feedbackFor.value === idx && feedbackKind.value === 'up') {
            feedbackFor.value = null
            feedbackKind.value = null
          }
        }, 1500)
      }
      // Thumbs-down opens the correction box; submission happens in
      // submitCorrection() once the user provides text.
    }
    const cancelFeedback = () => {
      feedbackFor.value = null
      feedbackKind.value = null
      correctionText.value = ''
    }
    const submitCorrection = async () => {
      const idx = feedbackFor.value
      const text = (correctionText.value || '').trim()
      if (idx == null || !text) {
        cancelFeedback()
        return
      }
      await submitFeedback(idx, 'down', text)
      cancelFeedback()
    }

    // ── Markdown / utils ──────────────────────────────────────────────
    // ── Message meta line ────────────────────────────────────────────
    // Renders "Strong portfolio match · 3.2s · 5 records" under each assistant
    // message that has any of those signals. Returns '' when none of the
    // metadata is present so the strip stays hidden on error/empty bubbles.
    const messageMetaLabel = (msg) => {
      const parts = []
      const q = (msg.evidenceQuality || '').toLowerCase()
      const qLabel = {
        strong: 'Strong portfolio match',
        moderate: 'Partial match',
        weak: 'Thin match',
        none: 'No records on file',
      }[q]
      if (qLabel) parts.push(qLabel)
      if (msg.elapsedMs && msg.elapsedMs > 0) {
        const secs = (msg.elapsedMs / 1000)
        parts.push(`${secs.toFixed(secs >= 10 ? 0 : 1)}s`)
      }
      const n = (msg.evidence || []).length
      if (n > 0) parts.push(`${n} record${n === 1 ? '' : 's'}`)
      return parts.join(' · ')
    }

    const qualityIcon = (q) => {
      switch ((q || '').toLowerCase()) {
        case 'strong':   return 'fas fa-shield-halved'
        case 'moderate': return 'fas fa-circle-check'
        case 'weak':     return 'fas fa-triangle-exclamation'
        case 'none':     return 'fas fa-circle-info'
        default:         return 'fas fa-circle-info'
      }
    }

    const renderMarkdown = (text) => {
      if (!text) return ''
      // Lightweight markdown: bold, italic, code, links, lists, headings, br
      let html = String(text)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
        .replace(/^### (.+)$/gm, '<h4>$1</h4>')
        .replace(/^## (.+)$/gm, '<h3>$1</h3>')
        .replace(/^# (.+)$/gm, '<h2>$1</h2>')
        .replace(/^- (.+)$/gm, '<li>$1</li>')
      html = html.replace(/(<li>.*<\/li>)(?!\s*<li>)/gs, '<ul>$1</ul>')
      html = html.replace(/\n\n/g, '<br><br>').replace(/\n/g, '<br>')
      return html
    }

    const copyMessage = (msg) => {
      navigator.clipboard?.writeText(msg.content || '')
    }

    const downloadMessage = (msg) => {
      const blob = new Blob([msg.content || ''], { type: 'text/markdown' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `compliance-consult-${Date.now()}.md`
      a.click()
      URL.revokeObjectURL(url)
    }

    const scrollToBottom = async () => {
      await nextTick()
      if (messagesEl.value) {
        messagesEl.value.scrollTop = messagesEl.value.scrollHeight
      }
    }

    const openFullConsult = () => {
      emit('open-full-consult')
    }

    // ── Token usage (debounced refresh after each send) ──────────────
    const loadTokenUsage = debounce(async () => {
      try {
        const res = await api.get('/users/billing/token_usage/')
        const data = res.data || {}
        if (data.monthly_limit) {
          tokenUsage.value = {
            used: data.tokens_used || 0,
            limit: data.monthly_limit,
            percent: Math.round(((data.tokens_used || 0) / data.monthly_limit) * 100),
          }
        }
      } catch (e) { /* non-fatal */ }
    }, 800)

    // ── Watch project change → new session ────────────────────────────
    watch(() => props.projectId, () => { newSession() })

    onMounted(async () => {
      await loadSessions()
      loadTokenUsage()
      if (props.initialQuery?.trim()) {
        sendExample(props.initialQuery.trim())
      }
    })

    defineExpose({
      sendExample,
      sendQuery: (queryText) => {
        composerText.value = queryText
        send()
      },
    })

    return {
      messages, composerText, sending, defaultDepth,
      currentSessionId, sessions, loadingSessions, showSessionsPanel,
      agentPhases, refinePrompt, engagementPrompt, featureGates, tokenUsage,
      feedbackFor, feedbackKind, correctionText,
      messagesEl, exampleQuestions, welcomeTitle, welcomeSub,
      placeholder, usageMeterClass,
      groupedSessions,
      loadSessions, loadSession, onSelectSession, deleteSession, newSession, saveCurrentSession,
      send, sendExample, applyRefine, applyEngagement,
      executeAction, visibleActions, confirmActions, onWriteCommitted,
      excelDownloadLabel, downloadExcel,
      recordFeedback, cancelFeedback, submitCorrection,
      messageMetaLabel, qualityIcon,
      renderMarkdown, copyMessage, downloadMessage,
      openFullConsult,
    }
  },
}
</script>

<style scoped>
.compliance-consult-chat {
  display: flex;
  flex-direction: row;
  height: 100%;
  min-height: 400px;
  background: var(--color-surface, #ffffff);
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--color-border, #e5e7eb);
}
.compliance-consult-chat.is-sidebar {
  border: none;
  border-radius: 0;
}

/* Sessions sidebar */
.sessions-sidebar {
  width: 240px;
  border-right: 1px solid var(--color-border, #e5e7eb);
  display: flex;
  flex-direction: column;
  background: var(--color-bg-subtle, #f9fafb);
  flex-shrink: 0;
}
.sessions-sidebar.collapsed { width: 48px; }

/* Dashboard-sidebar mode: slim rail by default, expands as an
   overlay on top of the chat when the user opens it. */
.sessions-sidebar.sidebar-mode {
  width: 36px;
  background: var(--color-surface, #fff);
}
.sessions-sidebar.sidebar-mode.overlay-open {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 240px;
  z-index: 30;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.08);
  background: var(--color-bg-subtle, #f9fafb);
}
.sessions-overlay-backdrop {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.18);
  z-index: 25;
}
/* Container needs to be positioned so the overlay anchors correctly. */
.compliance-consult-chat.is-sidebar { position: relative; }
.sidebar-header {
  display: flex;
  justify-content: space-between;
  padding: 8px;
  border-bottom: 1px solid var(--color-border, #e5e7eb);
}
.sessions-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}
.sessions-loading,
.sessions-empty {
  padding: 12px;
  text-align: center;
  color: var(--color-text-muted, #6b7280);
  font-size: 0.85rem;
}
.session-group { margin-bottom: 12px; }
.session-group-label {
  padding: 4px 12px;
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--color-text-muted, #6b7280);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.session-item {
  position: relative;
  padding: 8px 12px;
  cursor: pointer;
  border-left: 3px solid transparent;
  transition: background 0.15s;
}
.session-item:hover { background: var(--color-bg-hover, #f3f4f6); }
.session-item.active {
  background: var(--color-bg-active, #eff6ff);
  border-left-color: var(--color-primary, #3b82f6);
}
.session-title {
  font-size: 0.85rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.session-meta {
  font-size: 0.7rem;
  color: var(--color-text-muted, #6b7280);
  margin-top: 2px;
}
.session-delete {
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  color: var(--color-text-muted, #6b7280);
  cursor: pointer;
  display: none;
  padding: 2px 4px;
}
.session-item:hover .session-delete { display: block; }

/* Chat main */
.chat-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid var(--color-border, #e5e7eb);
}
.chat-header-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 0.95rem;
}
.chat-header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Depth toggle */
.depth-toggle {
  display: inline-flex;
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 6px;
  overflow: hidden;
  font-size: 0.8rem;
}
.depth-btn {
  padding: 4px 10px;
  background: var(--color-surface, #fff);
  border: none;
  cursor: pointer;
  color: var(--color-text-muted, #6b7280);
}
.depth-btn.active {
  background: var(--color-primary, #3b82f6);
  color: #fff;
}
.depth-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.btn-icon {
  background: none;
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 6px;
  padding: 4px 8px;
  cursor: pointer;
  color: var(--color-text-muted, #6b7280);
}
.btn-icon:hover { color: var(--color-primary, #3b82f6); }

/* Usage meter */
.usage-meter {
  padding: 6px 12px;
  font-size: 0.7rem;
  color: var(--color-text-muted, #6b7280);
  border-bottom: 1px solid var(--color-border, #e5e7eb);
}
.usage-bar {
  height: 4px;
  background: var(--color-border, #e5e7eb);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 4px;
}
.usage-fill {
  height: 100%;
  background: var(--color-primary, #3b82f6);
  transition: width 0.3s;
}
.usage-warning .usage-fill { background: #f59e0b; }
.usage-critical .usage-fill { background: #ef4444; }

/* Welcome state */
.welcome-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
}
.welcome-icon {
  font-size: 2.5rem;
  color: var(--color-primary, #3b82f6);
  margin-bottom: 12px;
}
.welcome-state h3 {
  margin: 0 0 8px;
  font-size: 1.1rem;
}
.welcome-sub {
  font-size: 0.85rem;
  color: var(--color-text-muted, #6b7280);
  max-width: 480px;
  margin-bottom: 16px;
}
.example-questions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  max-width: 420px;
}
.example-btn {
  padding: 8px 12px;
  border: 1px solid var(--color-border, #e5e7eb);
  background: var(--color-bg-subtle, #f9fafb);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  text-align: left;
}
.example-btn:hover { background: var(--color-bg-hover, #f3f4f6); }

/* Messages */
.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.message {
  display: flex;
  width: 100%;
}
.message.role-user { justify-content: flex-end; }
.message.role-assistant { justify-content: flex-start; }
.message-bubble {
  max-width: 85%;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 0.88rem;
  line-height: 1.45;
}
.message.role-user .message-bubble {
  background: var(--color-primary, #3b82f6);
  color: #fff;
}
.message.role-assistant .message-bubble {
  background: var(--color-bg-subtle, #f9fafb);
  border: 1px solid var(--color-border, #e5e7eb);
  width: 95%;
  max-width: 95%;
}
.message-content :deep(h2) { font-size: 1rem; margin: 8px 0; }
.message-content :deep(h3) { font-size: 0.95rem; margin: 6px 0; }
.message-content :deep(h4) { font-size: 0.9rem; margin: 4px 0; }
.message-content :deep(code) {
  background: rgba(0, 0, 0, 0.06);
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 0.85em;
}

/* Message meta strip — evidence quality, elapsed time, source count */
.message-meta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 0.72rem;
  color: var(--color-text-muted, #6b7280);
  padding: 3px 8px;
  border-radius: 999px;
  background: var(--color-bg-subtle, #f3f4f6);
  border: 1px solid var(--color-border, #e5e7eb);
}
.message-meta.quality-strong {
  color: #047857;
  background: #ecfdf5;
  border-color: #a7f3d0;
}
.message-meta.quality-moderate {
  color: #1d4ed8;
  background: #eff6ff;
  border-color: #bfdbfe;
}
.message-meta.quality-weak {
  color: #b45309;
  background: #fffbeb;
  border-color: #fde68a;
}
.message-meta.quality-none {
  color: #6b7280;
}

/* Actions row */
.actions-row {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: var(--color-primary, #3b82f6);
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.78rem;
}
.action-btn:hover { filter: brightness(1.05); }

/* Follow-ups */
.follow-ups {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}
.follow-ups-label {
  width: 100%;
  font-size: 0.75rem;
  color: var(--color-text-muted, #6b7280);
  font-weight: 600;
}
.follow-up-btn {
  padding: 5px 10px;
  background: var(--color-surface, #fff);
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 12px;
  cursor: pointer;
  font-size: 0.78rem;
}
.follow-up-btn:hover {
  background: var(--color-bg-hover, #f3f4f6);
}

/* Toolbar */
.message-toolbar {
  margin-top: 10px;
  display: flex;
  gap: 4px;
  border-top: 1px solid var(--color-border, #e5e7eb);
  padding-top: 6px;
}
.toolbar-btn {
  padding: 4px 8px;
  background: none;
  border: 1px solid transparent;
  border-radius: 4px;
  cursor: pointer;
  color: var(--color-text-muted, #6b7280);
  font-size: 0.8rem;
}
.toolbar-btn:hover { background: var(--color-bg-hover, #f3f4f6); }
.toolbar-btn.active {
  background: var(--color-bg-active, #eff6ff);
  color: var(--color-primary, #3b82f6);
}
.toolbar-btn:disabled { opacity: 0.4; cursor: not-allowed; }

/* Correction */
.correction-box { margin-top: 8px; }
.correction-box textarea {
  width: 100%;
  padding: 6px;
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 4px;
  font-size: 0.85rem;
  font-family: inherit;
  resize: vertical;
}
.correction-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 6px;
}
.btn-secondary,
.btn-primary {
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
  border: 1px solid var(--color-border, #e5e7eb);
}
.btn-secondary { background: var(--color-surface, #fff); }
.btn-primary {
  background: var(--color-primary, #3b82f6);
  color: #fff;
  border-color: var(--color-primary, #3b82f6);
}

/* Agent phases */
.agent-phases {
  background: var(--color-bg-subtle, #f9fafb);
  border: 1px dashed var(--color-border, #e5e7eb);
  border-radius: 8px;
  padding: 10px;
  font-size: 0.82rem;
}
.phase-line {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 0;
  color: var(--color-text-muted, #6b7280);
}
.phase-line.active { color: var(--color-primary, #3b82f6); font-weight: 500; }

/* Refine card */
.refine-card {
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 10px;
}
.refine-title {
  font-weight: 600;
  font-size: 0.85rem;
  margin-bottom: 6px;
  display: flex;
  gap: 6px;
  align-items: center;
}
.refine-suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.refine-chip {
  padding: 4px 10px;
  background: #fff;
  border: 1px solid #fde68a;
  border-radius: 12px;
  cursor: pointer;
  font-size: 0.78rem;
}
.refine-chip:hover { background: #fef3c7; }

/* Engagement (opt-in on weak results) */
.engagement-card {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 8px;
}
.engagement-title {
  font-weight: 600;
  font-size: 0.85rem;
  margin-bottom: 8px;
  display: flex;
  gap: 6px;
  align-items: center;
}
.engagement-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.engagement-btn {
  padding: 5px 12px;
  background: #fff;
  border: 1px solid #93c5fd;
  border-radius: 12px;
  cursor: pointer;
  font-size: 0.78rem;
  color: #1d4ed8;
}
.engagement-btn:hover { background: #dbeafe; }

/* Composer */
.composer {
  display: flex;
  border-top: 1px solid var(--color-border, #e5e7eb);
  padding: 8px;
  gap: 6px;
}
.composer-input {
  flex: 1;
  padding: 8px;
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 6px;
  font-size: 0.88rem;
  font-family: inherit;
  resize: none;
}
.composer-input:focus { outline: 2px solid var(--color-primary, #3b82f6); outline-offset: -1px; }
.send-btn {
  width: 40px;
  background: var(--color-primary, #3b82f6);
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}
.send-btn:disabled { opacity: 0.4; cursor: not-allowed; }

@media (max-width: 768px) {
  /* On phones, hide the hero sessions rail entirely; sidebar-mode
     keeps the slim rail so users can still reach history. */
  .compliance-consult-chat.is-hero .sessions-sidebar { display: none; }
  .compliance-consult-chat.is-hero { flex-direction: column; }
}
</style>
